import { useState, useEffect } from "react";
import { useAuth } from "../../components/useAuth";
import { useForm } from "react-hook-form";
import "./Announcement.css"; // 共通デザイン
import { useNavigate } from "react-router-dom";

const api_url = "https://api.ope-note.jp/admin/users.php";

const getGenderLabel = (val) => {
  if (val === "male" || val === "m") return "男";
  if (val === "female" || val === "f") return "女";
  return "―";
};

const getInvalidLabel = (val) => {
  if (val === 1 || val === "1") return "無効";
  return "有効";
};

const Users = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searching, setSearching] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: {},
  } = useForm();
  const [userCount, setUserCount] = useState({ userCount: 0, invalidCount: 0 });
  // 検索処理
  const onSearch = async (data) => {
    setError("");
    setInfoMessage("");
    setSearching(true);
    setSelectedUser(null);

    try {
      const response = await fetch(api_url, {
        method: "POST",
        mode: "cors",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: "search", // ★ PHP 側で実装する想定
          token: token,
          email: data.email || "",
          name01: data.name01 || "",
          name02: data.name02 || "",
        }),
      });

      if (!response.ok) {
        throw new Error(response.status);
      }

      const resData = await response.json();
      if (resData.result) {
        setUsers(resData.data || []);
        if (!resData.data || resData.data.length === 0) {
          setInfoMessage("該当するユーザーが見つかりませんでした。");
        }
      } else {
        setError(resData.error || "検索に失敗しました。");
        setUsers([]);
      }
    } catch (err) {
      if (err.message === "401" || err.message === "404") {
        navigate("/login");
      }
      console.log(err);
      setError("通信エラーです。");
      setUsers([]);
    } finally {
      setSearching(false);
    }
  };

  // 詳細取得
  const fetchDetail = async (id) => {
    setError("");
    setInfoMessage("");
    try {
      const response = await fetch(api_url, {
        method: "POST",
        mode: "cors",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: "detail", // ★ PHP 側：id で 1件取得
          token: token,
          id: id,
        }),
      });

      if (!response.ok) {
        throw new Error(response.status);
      }

      const resData = await response.json();
      if (resData.result) {
        setSelectedUser(resData.data);
      } else {
        setError(resData.error || "ユーザー情報の取得に失敗しました。");
      }
    } catch (err) {
      if (err.message === "401" || err.message === "404") {
        navigate("/login");
      }
      console.log(err);
      setError("通信エラーです。");
    }
  };

  // 有効/無効のトグル
  const handleToggleInvalid = async () => {
    if (!selectedUser) return;

    const currentInvalid =
      selectedUser.invalid === "1" || selectedUser.invalid === 1 ? 1 : 0;
    const newInvalid = currentInvalid === 1 ? 0 : 1;

    if (
      !window.confirm(
        `このユーザーを「${newInvalid === 1 ? "無効" : "有効"}」に変更しますか？`,
      )
    ) {
      return;
    }

    setUpdating(true);
    setError("");
    setInfoMessage("");

    try {
      const response = await fetch(api_url, {
        method: "POST",
        mode: "cors",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: "update_invalid", // ★ PHP 側：invalid の更新用
          token: token,
          id: selectedUser.id,
          invalid: newInvalid,
        }),
      });

      if (!response.ok) {
        throw new Error(response.status);
      }

      const resData = await response.json();
      if (resData.result) {
        // 画面側の state を更新
        const updatedUser = {
          ...selectedUser,
          invalid: newInvalid,
        };
        setSelectedUser(updatedUser);

        // 検索結果リスト側も同期
        setUsers((prev) =>
          prev.map((u) =>
            u.id === updatedUser.id
              ? { ...u, invalid: updatedUser.invalid }
              : u,
          ),
        );

        setInfoMessage("ステータスを更新しました。");
      } else {
        setError(resData.error || "更新に失敗しました。");
      }
    } catch (err) {
      if (err.message === "401" || err.message === "404") {
        navigate("/");
      }
      console.log(err);
      setError("通信エラーです。");
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    const getUserCount = async () => {
      try {
        const response = await fetch(api_url, {
          method: "POST",
          mode: "cors",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            mode: "count",
            token: token,
          }),
        });
        if (!response.ok) {
          throw new Error(response.status);
        }
        const resData = await response.json();
        setUserCount(resData.result);
      } catch (err) {
        setError("通信エラーです。");
      }
    };
    getUserCount();
  }, []);

  return (
    <div className="announcement-page">
      <div className="sub_title">ユーザー管理（有効:{userCount.userCount}／無効:{userCount.invalidCount}）</div>

      {/* 検索フォーム */}
      <div className="announcement-form-wrapper">
        <form className="frm ann-form-card" onSubmit={handleSubmit(onSearch)}>
          <h4 className="announcement-title">ユーザー検索</h4>

          {error && <div className="frm-error">{error}</div>}
          {infoMessage && <div className="frm-info">{infoMessage}</div>}

          <div className="frm-row">
            <label>メールアドレス</label>
            <input
              type="text"
              className="frm-input"
              placeholder="例）example@example.com"
              {...register("email")}
            />
          </div>

          <div className="frm-row frm-row-inline">
            <div className="frm-row-half">
              <label>姓 </label>
              <input
                type="text"
                className="frm-input"
                placeholder="例）山田"
                {...register("name02")}
              />
            </div>
            <div className="frm-row-half">
              <label>名 </label>
              <input
                type="text"
                className="frm-input"
                placeholder="例）太郎"
                {...register("name01")}
              />
            </div>
          </div>

          <div className="frm-footer">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={searching}
            >
              {searching ? "検索中..." : "検索する"}
            </button>
          </div>
        </form>
      </div>

      {/* 検索結果 ＋ 詳細表示 */}
      <div className="user-admin-layout">
        {/* 左側：検索結果リスト */}
        <div className="user-admin-list announcement-card">
          <div className="ann-row ann-row-header">
            <div className="ann-col ann-col-user-name">氏名</div>
            <div className="ann-col ann-col-user-email">メールアドレス</div>
            <div className="ann-col ann-col-user-invalid">状態</div>
          </div>
          {users.length > 0 ? (
            <div className="ann-table-body">
              {users.map((u) => (
                <button
                  type="button"
                  key={u.id}
                  className={
                    "ann-row ann-row-body user-row-button" +
                    (selectedUser && String(selectedUser.id) === String(u.id)
                      ? " user-row-button--active"
                      : "")
                  }
                  onClick={() => fetchDetail(u.id)}
                >
                  <div className="ann-col ann-col-user-name">
                    {u.name02} {u.name01}
                  </div>
                  <div className="ann-col ann-col-user-email" title={u.email}>
                    {u.email}
                  </div>
                  <div className="ann-col ann-col-user-invalid">
                    {getInvalidLabel(u.invalid)}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="ann-empty">検索結果がここに表示されます。</div>
          )}
        </div>

        {/* 右側：詳細表示＆有効/無効切り替え */}
        <div className="user-admin-detail announcement-card">
          <h4 className="announcement-title">ユーザー詳細</h4>

          {!selectedUser ? (
            <div className="ann-empty">
              ユーザーを検索し、一覧から選択してください。
            </div>
          ) : (
            <>
              <div className="frm-row">
                <label>ユーザーID</label>
                <div className="frm-static">{selectedUser.id}</div>
              </div>
              <div className="frm-row">
                <label>氏名</label>
                <div className="frm-static">
                  {selectedUser.name02} {selectedUser.name01}
                </div>
              </div>
              <div className="frm-row">
                <label>メールアドレス</label>
                <div className="frm-static">{selectedUser.email}</div>
              </div>
              <div className="frm-row">
                <label>性別</label>
                <div className="frm-static">
                  {getGenderLabel(selectedUser.gender)}
                </div>
              </div>
              <div className="frm-row">
                <label>手術内容</label>
                <div className="frm-static">
                  {selectedUser.user_category_name || "―"}
                </div>
              </div>
              <div className="frm-row">
                <label>状態</label>
                <div className="frm-static">
                  {getInvalidLabel(selectedUser.invalid)}
                </div>
              </div>

              <div className="frm-footer frm-footer-between">
                <div className="frm-info-text">
                  ※ この画面では有効／無効のみ変更できます。
                </div>
                <button
                  type="button"
                  className={
                    selectedUser.invalid === 1 || selectedUser.invalid === "1"
                      ? "btn btn-primary"
                      : "btn btn-danger"
                  }
                  onClick={handleToggleInvalid}
                  disabled={updating}
                >
                  {updating
                    ? "更新中..."
                    : selectedUser.invalid === 1 || selectedUser.invalid === "1"
                      ? "有効にする"
                      : "無効にする"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Users;
